<template>
    <div>
        <UiCardListHeader
            :title="lang('congresses::congresses.widgets.upcoming.title')"
            :action-href="route('congresses.admin.index')"
            :action-label="lang('congresses::congresses.actions.manage')"
        />

        <div class="grid gap-4 md:grid-cols-2 upcoming-congresses__months">
            <div v-for="month in months" :key="month.key" class="space-y-2">
                <h3 class="text-sm font-semibold text-neutral-600 dark:text-neutral-400 upcoming-congresses__month">
                    {{ month.name }} {{ month.year }}
                </h3>

                <div
                    v-if="month.congresses.length === 0"
                    class="py-3 text-sm text-center text-neutral-400 dark:text-neutral-500"
                >
                    {{ lang('congresses::congresses.widgets.upcoming.no_congresses') }}
                </div>

                <div v-else class="space-y-2">
                    <UiCard
                        v-for="congress in month.congresses"
                        :key="congress.id"
                        interactive
                        class="flex items-center px-4 py-3 text-sm cursor-pointer"
                        @click="openCongress(congress)"
                    >
                        <div class="flex-auto">
                            <div class="font-semibold">
                                {{ congress.title }}
                            </div>
                            <div class="flex items-center gap-3 mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                                <span v-if="congress.dates_display" class="inline-flex items-center gap-1">
                                    <icon icon="hero/outline/calendar" class="size-3" />
                                    {{ congress.dates_display }}
                                </span>
                                <span v-if="congress.location_city" class="inline-flex items-center gap-1">
                                    <icon icon="hero/outline/map-pin" class="size-3" />
                                    {{ congress.location_city }}
                                </span>
                                <a
                                    :href="attendeesUrl(congress.id)"
                                    class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-neutral-100 dark:bg-neutral-800 hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors"
                                    @click.stop
                                >
                                    <icon icon="hero/outline/user-group" class="size-3" />
                                    <span
                                        v-tooltip="{ message: lang('congresses::congresses.widgets.upcoming.counts.paid'), position: 'top' }"
                                        class="text-green-600 dark:text-green-400"
                                    >{{ congress.paid_attendees_count ?? 0 }}</span>
                                    <span class="text-neutral-400">/</span>
                                    <span
                                        v-tooltip="{ message: lang('congresses::congresses.widgets.upcoming.counts.present'), position: 'top' }"
                                        class="text-blue-600 dark:text-blue-400"
                                    >{{ congress.checked_in_attendees_count ?? 0 }}</span>
                                    <span class="text-neutral-400">/</span>
                                    <span
                                        v-tooltip="{ message: lang('congresses::congresses.widgets.upcoming.counts.total'), position: 'top' }"
                                    >{{ congress.attendees_count ?? 0 }}</span>
                                </a>
                            </div>
                        </div>
                        <div class="grow-0">
                            <icon icon="hero/outline/chevron-right" class="size-5 text-neutral-400" />
                        </div>
                    </UiCard>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import type { Ref } from 'vue';
import { router } from '@inertiajs/vue3';

interface Congress {
    id: number;
    title: string;
    slug: string;
    dates_display?: string;
    location_city?: string;
    attendees_count?: number;
    paid_attendees_count?: number;
    checked_in_attendees_count?: number;
}

interface Month {
    key: string;
    name: string;
    year: number;
    congresses: Congress[];
}

interface WidgetData {
    months?: Month[];
}

const props = defineProps<{ slug?: string; settings?: object; data?: WidgetData }>();

const months: Ref<Month[]> = ref(props.data?.months ?? []);

const openCongress = (congress: Congress): void => {
    router.visit(route('congresses.admin.edit', { id: congress.id }));
};

const attendeesUrl = (congressId: number): string => {
    const filters = JSON.stringify([{ field: 'congress_id', operator: '=', value: congressId }]);
    return route('congresses/attendees.admin.index') + '?filters=' + encodeURIComponent(filters);
};
</script>
